import React, { RefObject } from "react";
import { Box, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ReplayIcon from "@mui/icons-material/Replay";
import { Trans } from "react-i18next";
import {
    DotLegalHeader,
    DotLegalLink,
    DotLegalOverflowMenu,
    DotLegalOverflowMenuItem,
    DotLegalTextField,
    DotLegalTooltip,
} from "@dotlegal/dotlegal-ui-components";
import { isNullOrWhitespace } from "@common/stringOperations";
import { useTranslation } from "@localization/useTranslation";
import { annualWheelActivityIcon, obligationIcon } from "@common/icons";
import { usePermissionsProvider } from "../../../auth/usePermissionsProvider";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useUrlProvider } from "../../../useUrlProvider";
import { ActivityPaneMode } from "../../../annualWheel/activityPane/ActivityPane.types";
import { AnnualWheelActivityTaskNavigationViewModel, TaskViewModel } from "../TaskPaneContent.types";
import { useTaskPaneHeaderStyles } from "./TaskPaneHeader.styles";
import TaskNavigationLinkButton from "./TaskNavigationLinkButton";
import { useTaskTitleEditing } from "./TaskPaneHeader.hooks";
import { TaskRawStateEnum } from "../../../annualWheel/AnnualWheel.types";

export interface BackToOpenedTask {
    taskId: string;
    deadline?: Date;
}

export interface OpenableActivity {
    annualWheelActivityId: string;
    contractId?: string;
    /** Read for a user who may see the activity but not change it, whose pane then has no save button. */
    mode: ActivityPaneMode;
}

/**
 * The activity behind the task, and whether the user may change it. An obligation answers to the contract
 * it belongs to, a plain activity to the annual wheel, so the two are governed by different permissions.
 * Lacking the edit permission is not a reason to withhold the activity: seeing which activity a task came
 * from is part of carrying the task out, so it opens read-only instead of not at all.
 */
export function useActivityToOpen(task: TaskViewModel | undefined): OpenableActivity | undefined {
    const { permissions } = useUserContext();
    const { hasAnnualWheelActivityAccess } = usePermissionsProvider();

    const contractPermissions = permissions.contractManagementPermissions.permissions;
    const activityPermissions = task?.contractId ? { ...contractPermissions, delete: contractPermissions.edit } : permissions.annualWheelPermission;
    // Both ids are Guid? on the wire, so a task without them sends null rather than leaving them out, and
    // an undefined check would treat an ad hoc task as though it had an activity behind it.
    const annualWheelActivityId = task?.annualWheelActivityId || undefined;

    // The same rule the server applies in EnsureCanReadActivity: an activity that belongs to a contract is
    // read through that contract, so annual wheel access does not come into it, and gating on it here hid
    // every obligation from a user who may read its contract but has no annual wheel of their own.
    const canReadActivity = task?.contractId ? contractPermissions.read : hasAnnualWheelActivityAccess();

    if (annualWheelActivityId === undefined || !canReadActivity) {
        return undefined;
    }

    return { annualWheelActivityId, contractId: task?.contractId || undefined, mode: activityPermissions.edit ? "edit" : "read" };
}

export interface ITaskPaneHeaderProps {
    task: TaskViewModel | undefined;
    onNameChange?: (value: string) => void;
    titleRef?: RefObject<HTMLDivElement | null>;
    /** Left out by a host that cannot show a right pane, which keeps the plain navigation link instead. */
    onOpenObligation?: (activity: OpenableActivity) => void;
}

/** The task's name and the activity it belongs to. Stepping between siblings is TaskPaneSiblingNavigation. */
export function TaskPaneHeader(props: ITaskPaneHeaderProps) {
    const { translateString } = useTranslation();
    const styles = useTaskPaneHeaderStyles();
    const { permissions } = useUserContext();
    const { getProcessingActivityValidationUrl } = useUrlProvider();
    const activityToOpen = useActivityToOpen(props.task);
    const { isEditingTitle, startEditingTitle, stopEditingTitle } = useTaskTitleEditing();
    const task = props.task;

    if (!task) {
        return null;
    }

    const canOpenActivity = activityToOpen !== undefined && props.onOpenObligation !== undefined;
    const activityIcon = task.contractId ? (
        <DotLegalTooltip text={translateString("connectedToObligation")}>{obligationIcon}</DotLegalTooltip>
    ) : (
        <DotLegalTooltip text={translateString("connectedToAnnualWheelActivity")}>{annualWheelActivityIcon}</DotLegalTooltip>
    );

    // A risk mitigation task's title is owned by its plan (name + scenario), so it is locked and gets a fixed,
    // translated "Risk Mitigation:" label, mirroring how a processing activity validation task is presented.
    const isRiskMitigation = Boolean(task.riskMitigationPlanId);
    const displayName = isRiskMitigation ? `${translateString("riskMitigationTaskLabel")}: ${task.name}` : task.name;

    return (
        <Box sx={styles.titleBlock} ref={props.titleRef}>
            {task.isAdHocTask && permissions.taskPermissions.edit && !isRiskMitigation ? (
                <Box sx={{ display: "flex", gap: 1 }}>
                    {task.customId && (
                        <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                            {task.customId}
                        </DotLegalHeader>
                    )}
                    {/* An empty title has nothing to click on, so it stays a field. */}
                    {isEditingTitle || isNullOrWhitespace(task.name) ? (
                        // React's blur bubbles, so leaving the field is caught here. The field's own blur runs
                        // first and saves the debounced value before the title folds back onto one line.
                        <Box
                            onBlur={stopEditingTitle}
                            sx={(theme) => ({
                                "& .MuiInputBase-input": {
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    color: theme.palette.primary.dark,
                                },
                                flex: 1,
                                minWidth: 0,
                                marginTop: "-0.7px",
                            })}
                        >
                            <DotLegalTextField
                                disabled={!permissions.taskPermissions.edit}
                                noMargin
                                multiline
                                autoFocus={isEditingTitle}
                                onChange={props.onNameChange}
                                variant="standard"
                                label={""}
                                value={task.name ?? ""}
                                debounce
                            />
                        </Box>
                    ) : (
                        // A long title is kept to one line until it is edited, so the header stays one row high.
                        <Box tabIndex={0} onFocus={startEditingTitle} sx={{ flex: 1, minWidth: 0, cursor: "text" }}>
                            <DotLegalHeader marginBottom={0} headerStyle={"small"} maxNoLines={1}>
                                {task.name}
                            </DotLegalHeader>
                        </Box>
                    )}
                </Box>
            ) : (
                <Box sx={styles.headerContainer}>
                    {task.customId && (
                        <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                            {task.customId}
                        </DotLegalHeader>
                    )}
                    {/* A flex item will not shrink below its content without a zero min-width, so without it a
                        long title would push past the header instead of ending in an ellipsis. */}
                    <Box sx={{ minWidth: 0 }}>
                        <DotLegalHeader marginBottom={0} headerStyle={"small"} maxNoLines={1}>
                            {displayName}
                        </DotLegalHeader>
                    </Box>
                    {task.processingActivityName && (
                        <React.Fragment>
                            <Typography sx={styles.seperator}>/</Typography>
                            <DotLegalHeader marginBottom={0} color="secondary" headerStyle={"small"}>
                                {task.processingActivityId ? (
                                    <DotLegalLink to={getProcessingActivityValidationUrl(task.processingActivityId)}>
                                        {task.processingActivityName}
                                    </DotLegalLink>
                                ) : (
                                    <span>{task.processingActivityName}</span>
                                )}
                            </DotLegalHeader>
                        </React.Fragment>
                    )}
                    {(!task.isAdHocTask || task.contractId) && (
                        <Box
                            sx={{
                                "& svg": {
                                    width: 20,
                                    height: 20,
                                },
                            }}
                        >
                            {/* Without read access the activity answers 403, which sends the user to the forbidden
                                page, so the icon keeps only its tooltip. */}
                            {canOpenActivity ? (
                                <DotLegalLink onClick={() => props.onOpenObligation!(activityToOpen!)}>{activityIcon}</DotLegalLink>
                            ) : (
                                activityIcon
                            )}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}

export interface ITaskPaneSiblingNavigationProps {
    activityNavigation: AnnualWheelActivityTaskNavigationViewModel;
    backToOpenedTask?: BackToOpenedTask;
    onNavigateToTask: (taskId: string) => void;
}

/** Stepping between the tasks of the activity this one belongs to. */
export function TaskPaneSiblingNavigation(props: ITaskPaneSiblingNavigationProps) {
    const { translateString, translateDate } = useTranslation();
    const styles = useTaskPaneHeaderStyles();
    const { getTaskManagementTaskDialogUrl } = useUrlProvider();

    const renderTaskStep = (
        taskId: string | null | undefined,
        deadline: Date | string | null | undefined,
        tooltipKey: string,
        icon: React.ReactElement,
        color?: "primary"
    ) => (
        <TaskNavigationLinkButton
            linkTo={taskId ? getTaskManagementTaskDialogUrl(taskId) : undefined}
            onNavigate={() => props.onNavigateToTask(taskId!)}
            color={color}
            tooltip={deadline && <Trans i18nKey={tooltipKey} values={{ deadline: translateDate(new Date(deadline)) }} />}
        >
            {icon}
        </TaskNavigationLinkButton>
    );

    return (
        <Box sx={styles.activityNavigation}>
            {renderTaskStep(
                props.activityNavigation.previousTaskId,
                props.activityNavigation.previousDeadline,
                "previousTaskOnActivity",
                <ChevronLeftIcon fontSize="small" />
            )}
            <Typography sx={styles.activityNavigationPosition}>
                {translateString("taskPositionOnActivity", {
                    position: props.activityNavigation.position,
                    total: props.activityNavigation.total,
                })}
            </Typography>
            {renderTaskStep(
                props.activityNavigation.nextTaskId,
                props.activityNavigation.nextDeadline,
                "nextTaskOnActivity",
                <ChevronRightIcon fontSize="small" />
            )}
            {props.backToOpenedTask &&
                renderTaskStep(props.backToOpenedTask.taskId, props.backToOpenedTask.deadline, "backToOpenedTask", <ReplayIcon fontSize="small" />, "primary")}
        </Box>
    );
}

export interface ITaskPaneHeaderActionsProps {
    task: TaskViewModel | undefined;
    onDeleteClick: () => void;
    onSendReminderClick: () => void;
    onDownloadClick: () => void;
    onOpenObligation?: (activity: OpenableActivity) => void;
}

/** Delete, send reminder, download and, where the user may edit it, the activity behind the task. */
export function TaskPaneHeaderActions(props: ITaskPaneHeaderActionsProps) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const activityToOpen = useActivityToOpen(props.task);

    if (!props.task) {
        return null;
    }

    const menuItems = Array<DotLegalOverflowMenuItem>();

    if (permissions.taskPermissions.delete) {
        menuItems.push({ menuItem: translateString("delete"), onClick: props.onDeleteClick });
    }

    // A reminder only makes sense once the deadline has passed and nobody has completed the task.
    if (permissions.taskPermissions.edit && props.task.isOverdue && props.task.taskState !== TaskRawStateEnum.Completed) {
        menuItems.push({ menuItem: translateString("sendReminder"), onClick: props.onSendReminderClick });
    }

    menuItems.push({ menuItem: translateString("download"), onClick: props.onDownloadClick });

    if (activityToOpen && props.onOpenObligation) {
        menuItems.push({
            menuItem: translateString(activityToOpen.mode === "edit" ? "editActivity" : "seeActivity"),
            onClick: () => props.onOpenObligation!(activityToOpen),
        });
    }

    return <DotLegalOverflowMenu setHorizontally color={"secondary"} items={menuItems} />;
}
